import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Users, BookOpen, ShoppingCart, DollarSign, Trash2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showClearRevenue, setShowClearRevenue] = useState(false);
  const [clearingRevenue, setClearingRevenue] = useState(false);

  const handleClearTestData = async () => {
    setClearing(true);
    try {
      const res = await fetch('/api/admin/clear-test-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: 'CLEAR_ALL_TEST_DATA' }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('All test data cleared successfully');
        queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      } else {
        toast.error(data.error || 'Failed to clear data');
      }
    } catch (err) {
      toast.error('Failed to clear test data');
    } finally {
      setClearing(false);
      setShowClearConfirm(false);
    }
  };

  const handleClearRevenue = async () => {
    setClearingRevenue(true);
    try {
      const res = await fetch('/api/admin/clear-revenue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: 'CLEAR_REVENUE' }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Revenue reset to zero');
        queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      } else {
        toast.error(data.error || 'Failed to clear revenue');
      }
    } catch (err) {
      toast.error('Failed to clear revenue data');
    } finally {
      setClearingRevenue(false);
      setShowClearRevenue(false);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => adminApi.getDashboard(),
  });

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;

  const stats = [
    { label: 'Total Users', value: data?.stats?.totalUsers || 0, icon: Users, color: 'bg-blue-100 text-blue-600' },
    { label: 'Total Courses', value: data?.stats?.totalCourses || 0, icon: BookOpen, color: 'bg-green-100 text-green-600' },
    { label: 'Total Enrollments', value: data?.stats?.totalEnrollments || 0, icon: ShoppingCart, color: 'bg-purple-100 text-purple-600' },
    { label: 'Total Revenue', value: (data?.stats?.totalRevenue || 0).toLocaleString() + ' IQD', icon: DollarSign, color: 'bg-orange-100 text-orange-600' },
  ];

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('admin.dashboard')}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-muted-foreground text-sm">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Action Buttons */}
      {!showClearConfirm && !showClearRevenue && (
        <div className="mb-6 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClearRevenue(true)}
            className="text-orange-500 border-orange-200 hover:bg-orange-50 hover:text-orange-600"
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Clear Revenue
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowClearConfirm(true)}
            className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Test Data
          </Button>
        </div>
      )}

      {/* Clear Revenue Confirmation */}
      {showClearRevenue && (
        <div className="mb-6 p-4 rounded-lg border-2 border-orange-200 bg-orange-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-orange-700 mb-1">Clear Revenue Data?</p>
              <p className="text-sm text-orange-600 mb-3">
                This will reset the <strong>Total Revenue</strong> counter to zero. All orders, payment logs, and other records will remain untouched.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={handleClearRevenue}
                  disabled={clearingRevenue}
                >
                  {clearingRevenue ? 'Clearing...' : 'Yes, Clear Revenue'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowClearRevenue(false)}
                  disabled={clearingRevenue}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Test Data Confirmation */}
      {showClearConfirm && (
        <div className="mb-6 p-4 rounded-lg border-2 border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-700 mb-1">Are you sure?</p>
              <p className="text-sm text-red-600 mb-3">
                This will permanently delete all <strong>orders, payment logs, enrollments, lesson progress, and certificates</strong>. This cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleClearTestData}
                  disabled={clearing}
                >
                  {clearing ? 'Clearing...' : 'Yes, Clear Everything'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowClearConfirm(false)}
                  disabled={clearing}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Recent Orders</CardTitle></CardHeader>
          <CardContent>
            {data?.recentOrders?.length > 0 ? (
              <div className="space-y-3">
                {data.recentOrders.map((order: any) => (
                  <div key={order.id} className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <div>
                      <p className="font-medium">{order.user?.name}</p>
                      <p className="text-sm text-muted-foreground">{order.course?.titleEn}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${order.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {order.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No recent orders</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Popular Courses</CardTitle></CardHeader>
          <CardContent>
            {data?.popularCourses?.length > 0 ? (
              <div className="space-y-3">
                {data.popularCourses.map((course: any) => (
                  <div key={course.id} className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <p className="font-medium">{course.titleEn}</p>
                    <span className="text-sm text-muted-foreground">{course.enrollmentCount} students</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No courses yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
